/**
 * A specialist's evaluation on the Teams page (docs/evaluation.md): the latest result in words with one
 * mark, an Evaluate action for administrators and the specialist's owner, earlier results in a
 * disclosure, and the dialog that asks one node to run the specialist on a dataset. Words come from
 * specialistEvaluationWords.ts.
 */

import { isRedTeam, type EvaluationDataset, type SpecialistEvaluations } from '@aioe/shared';
import { dateShort } from '@aioe/shared/wording/dateTime';
import { Modal } from '@heroui/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { problemText, routes, type TeamWhereRow } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { Button, ChoiceSelect, ErrorNote, inputClass, StatusMark } from './ui.tsx';
import { evaluationLine, evaluationMark, evaluationMeta, hasProjectOpen, orderWords, percent, staleWords } from './specialistEvaluationWords.ts';

/** The specialist's row part: nothing for a team. */
export function SpecialistEvaluation({ t, onChanged }: { t: TeamWhereRow; onChanged: () => void }) {
  const [asking, setAsking] = useState(false);
  if (t.kind !== 'specialist') return null;
  const e = t.evaluation;
  const mark = e ? evaluationMark(e) : undefined;
  const pending = t.evaluating?.state === 'pending';
  const order = orderWords(t.evaluating, !!e);
  return (
    <div className="grid gap-1">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          {mark ? <StatusMark state={mark.state} label={mark.label} /> : null}
          <span className={e ? 'text-sm' : 'text-sm text-muted'}>{e ? `${evaluationLine(e)}.` : 'Not evaluated yet.'}</span>
        </div>
        {t.mayEvaluate ? (
          <Button size="sm" variant="ghost" isDisabled={pending} onPress={() => setAsking(true)}>
            Evaluate
          </Button>
        ) : null}
      </div>
      {e?.stale ? <p className="m-0 text-xs text-muted">Against {e.datasetName}. The specialist or the dataset has changed since; evaluate it again for a current result.</p> : e ? <p className="m-0 text-xs text-muted">Against {e.datasetName}.</p> : null}
      {order ? (
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted">
          {pending ? <StatusMark state="working" label="Pending" /> : null}
          <span>{order}</span>
        </div>
      ) : null}
      {e ? <EarlierResults id={t.id} /> : null}
      {asking ? <EvaluateSpecialistDialog specialist={{ id: t.id, name: t.name }} onClose={() => setAsking(false)} onIssued={onChanged} /> : null}
    </div>
  );
}

/** Results so far, newest first, loaded when opened. */
function EarlierResults({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  return (
    <details className="group text-xs" onToggle={(ev) => setOpen((ev.currentTarget as HTMLDetailsElement).open)}>
      <summary className="w-fit cursor-[var(--cursor-interactive)] list-none text-muted hover:text-foreground [&::-webkit-details-marker]:hidden">Results so far</summary>
      {open ? <History id={id} /> : null}
    </details>
  );
}

function History({ id }: { id: string }) {
  const h = useLoad<SpecialistEvaluations>(() => routes.specialistEvaluations(id), [id]);
  if (h.error) return <p className="m-0 mt-2 text-muted">{h.error}</p>;
  if (!h.data) return <p className="m-0 mt-2 text-muted">Loading…</p>;
  return (
    <ul className="mt-2 grid gap-1.5">
      {h.data.history.map((r, i) => (
        <li key={r.id} className="grid gap-0.5">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
            <span className="tabular-nums">{dateShort(r.at)}</span>
            <span className="min-w-0">{r.datasetName}</span>
            <span className="tabular-nums">{percent(r.result.passRate)} pass</span>
            <span className="text-muted">{r.result.verdict === 'pass' ? 'passed' : 'did not pass'}{r.stale ? ', stale' : ''}</span>
          </div>
          {r.result.sentence ? <p className="m-0 text-muted">{r.result.sentence.replace(/\.?$/, '.')}</p> : null}
          {i === 0 ? <p className="m-0 text-muted">{evaluationMeta(r)}{r.stale ? ` ${staleWords(r.changed)}` : ''}</p> : null}
        </li>
      ))}
    </ul>
  );
}

/** Ask one online node to run a specialist on an evaluation dataset. */
export function EvaluateSpecialistDialog({ specialist, onClose, onIssued }: { specialist: { id: string; name: string }; onClose: () => void; onIssued: () => void }) {
  const nodes = useLoad(() => routes.nodes(), []);
  const catalogue = useLoad(() => routes.evaluationDatasets(), []);
  const online = (nodes.data ?? []).filter((n) => n.kind === 'node' && n.online && (n.approval.state === 'approved' || n.approval.state === 'pending-surface')).sort((a, b) => Number(hasProjectOpen(b)) - Number(hasProjectOpen(a)) || a.hostname.localeCompare(b.hostname));
  const nodeOptions = Object.fromEntries(online.map((n) => [n.deviceId, hasProjectOpen(n) ? `${n.hostname} (project open)` : n.hostname])) as Record<string, string>;
  const datasets = (catalogue.data?.entries ?? []).filter((e) => e.kind === 'evaluation');
  const datasetOptions = Object.fromEntries(
    datasets.map((d) => {
      const c = d.content as EvaluationDataset | null;
      const n = c?.cases?.length ?? 0;
      return [d.id, `${d.name} (${n} ${n === 1 ? 'case' : 'cases'}${c && isRedTeam(c) ? ', red team' : ''})`];
    }),
  ) as Record<string, string>;
  const [deviceId, setDeviceId] = useState<string>();
  const [datasetId, setDatasetId] = useState<string>();
  const [worker, setWorker] = useState('');
  const [judge, setJudge] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>();
  const chosen = deviceId && nodeOptions[deviceId] ? deviceId : online[0]?.deviceId;
  const chosenNode = online.find((n) => n.deviceId === chosen);
  // A dataset written for this specialist comes first.
  const forThis = datasets.find((d) => (d.content as EvaluationDataset | null)?.for?.specialistId === specialist.id);
  const dataset = datasetId && datasetOptions[datasetId] ? datasetId : (forThis ?? datasets[0])?.id;

  async function send() {
    if (!chosen || !dataset) return;
    setSending(true);
    setError(undefined);
    try {
      await routes.evaluateSpecialist(specialist.id, { deviceId: chosen, datasetId: dataset, ...(worker.trim() ? { worker: worker.trim() } : {}), ...(judge.trim() ? { judge: judge.trim() } : {}) });
      onIssued();
      onClose();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal isOpen onOpenChange={(open) => !open && onClose()}>
      <Modal.Backdrop>
        <Modal.Container placement="center" size="md">
          <Modal.Dialog aria-label={`Evaluate ${specialist.name}`}>
            <Modal.Header>
              <Modal.Heading>Evaluate {specialist.name}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="grid gap-3 p-1">
              <p className="m-0 text-sm text-muted">The node runs the specialist on each case of the dataset, and a judge model scores each answer against what the case expects. Nothing is published or granted on the result.</p>
              {nodes.data && !online.length ? (
                <p className="m-0 text-sm">No node is online. A specialist is evaluated on a node, unattended: bring one online and try again.</p>
              ) : (
                <ChoiceSelect label="Node" value={chosen} options={nodeOptions} onChange={setDeviceId} placeholder={nodes.data ? 'Choose a node' : 'Loading nodes…'} isDisabled={!online.length} />
              )}
              {chosenNode && !hasProjectOpen(chosenNode) ? <p className="m-0 text-xs text-muted">This node has no project open, so it will refuse the evaluation until one of its workbenches runs.</p> : null}
              {catalogue.data && !datasets.length ? (
                <p className="m-0 text-sm">
                  No evaluation dataset is published yet. Add one on the <Link to="/catalogue" className="text-accent hover:underline">Catalogue</Link> page as an evaluation dataset.
                </p>
              ) : (
                <ChoiceSelect label="Dataset" value={dataset} options={datasetOptions} onChange={setDatasetId} placeholder={catalogue.data ? 'Choose a dataset' : 'Loading datasets…'} isDisabled={!datasets.length} />
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1 text-xs text-muted">
                  Worker model (optional)
                  <input className={`${inputClass} mono`} value={worker} onChange={(e) => setWorker(e.target.value)} placeholder="The node’s own" />
                </label>
                <label className="grid gap-1 text-xs text-muted">
                  Judge model (optional)
                  <input className={`${inputClass} mono`} value={judge} onChange={(e) => setJudge(e.target.value)} placeholder="The node’s own" />
                </label>
              </div>
              <ErrorNote error={error ?? catalogue.error} />
            </Modal.Body>
            <Modal.Footer>
              <Button size="sm" variant="tertiary" onPress={onClose}>
                Cancel
              </Button>
              <Button size="sm" isPending={sending} isDisabled={!chosen || !dataset} onPress={() => void send()}>
                Evaluate
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
